import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth, authService } from "auth";
import { AuthFormField } from "components/auth-form/AuthForm";
import { setTreasuryId, setUserType } from "store/userSlice";
import { apiErrorMessage } from "utils/errors/parseApiError";

// const EMAIL_RE = /\S+@\S+\.\S+/;
const ADMIN_EMAIL = "admin@thirdwayv.com";
const ADMIN_PASSWORD = "12345678";

export interface UseLoginFormReturn {
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  handleLogin: () => Promise<void>;
  handleDummyLogin: () => Promise<void>;
  handleCloseError: () => void;
}

export function useLoginForm(): UseLoginFormReturn {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    signIn,
    loading: authLoading,
    error: authError,
    clearError,
  } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const passwordRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    clearError();
    setShowLoading(false);
    setShowError(false);
    setErrorMsg("");
  };

  const resetForm = () => {
    setUsername("");
    setPassword("");
  };

  const handleDummyLogin = async () => {
    try {
      setLoadingMsg("Signing in…");
      setShowLoading(true);
      if (username === "admin" && password === "admin")
        await signIn(ADMIN_EMAIL, ADMIN_PASSWORD);
      else await signIn(username, password);
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        setShowLoading(false);
        return;
      }
      const profile = await authService.getUserProfile(currentUser.uid);
      const treasuryId = profile.treasuryId ?? username;

      dispatch(setUserType("admin"));
      dispatch(setTreasuryId(treasuryId));
      setShowLoading(false);
      navigate("/issuer", {
        replace: true,
      });
    } catch (error: any) {
      resetForm();
      setShowLoading(false);
      openError(
        apiErrorMessage(error, "Invalid credentials. Please check your details."),
      );
    }
  };
  const handleLogin = async () => {
    try {
      let identifier = username.trim().replace(/\s/g, "");

      setLoadingMsg("Signing in…");
      setShowLoading(true);
      await signIn(identifier, password);

      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        setShowLoading(false);
        return;
      }
      const profile = await authService.getUserProfile(currentUser.uid);
      const treasuryId = profile.treasuryId ?? username;

      // Every route in this app requires userType "admin" (App.tsx); without this
      // the guard bounces a freshly signed-in issuer straight back to the login form.
      dispatch(setUserType("admin"));
      dispatch(setTreasuryId(treasuryId));
      setShowLoading(false);
      navigate("/issuer", { replace: true });
    } catch (err: any) {
      resetForm();
      setShowLoading(false);
      dispatch(setUserType(undefined));
      openError(
        apiErrorMessage(err, "Invalid credentials. Please check your details."),
      );
    }
  };

  const focusPassword = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") passwordRef.current?.focus();
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const fields: AuthFormField[] = [
    {
      name: "username",
      label: "Email / ID",
      value: username,
      onChange: setUsername,
      onKeyDown: focusPassword,
      placeholder: "Enter your email or ID",
      autoComplete: "username",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      value: password,
      onChange: setPassword,
      onKeyDown: submitOnEnter,
      placeholder: "Enter your password",
      autoComplete: "current-password",
      inputRef: passwordRef,
    },
  ];

  const isShowingLoading = showLoading || authLoading;
  const isShowingError = showError || Boolean(authError);
  const resolvedErrorMsg = showError ? errorMsg : (authError?.message ?? "");

  return {
    fields,
    submitRef,
    isSubmitDisabled: !username || !password,
    showLoading: isShowingLoading,
    loadingMsg,
    showError: isShowingError,
    errorMsg: resolvedErrorMsg,
    handleLogin,
    handleDummyLogin,
    handleCloseError,
  };
}
